import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';

const MyConversations = () => {
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [showReplyBox, setShowReplyBox] = useState(false);
  const messagesEndRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const [botPaused, setBotPaused] = useState(false);
  const [botPauseRemaining, setBotPauseRemaining] = useState(null);
  const [botPauseWarning, setBotPauseWarning] = useState(null);
  const botPauseTimerRef = useRef(null);

  // 🆕 Bot pause countdown timer
  useEffect(() => {
    if (botPaused && botPauseRemaining > 0) {
      botPauseTimerRef.current = setInterval(() => {
        setBotPauseRemaining(prev => {
          if (prev <= 1) {
            setBotPaused(false);
            clearInterval(botPauseTimerRef.current);
            return null;
          }
          // Show warning at 60 seconds
          if (prev === 60) {
            setBotPauseWarning('⚠️ البوت سيعود للعمل خلال دقيقة!');
            setTimeout(() => setBotPauseWarning(null), 10000);
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (botPauseTimerRef.current) {
        clearInterval(botPauseTimerRef.current);
      }
    };
  }, [botPaused, botPauseRemaining]);
  
  // ✅ SINGLE useEffect for selectedConversation (removed duplicate)
  useEffect(() => {
    if (selectedConversation) {
      const convId = selectedConversation.conversation_id || selectedConversation.id;
      fetchMessages(convId);
      checkBotPauseStatus(convId);
      
      refreshIntervalRef.current = setInterval(() => {
        fetchMessages(convId, true);
        checkBotPauseStatus(convId);
      }, 5000);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [selectedConversation]);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    fetchMyAssignments();
  }, []);

  // Handle opening specific conversation from navigation state
  useEffect(() => {
    if (location.state?.openConversationId && conversations.length > 0) {
      const conversationToOpen = conversations.find(
        conv => conv.conversation_id === location.state.openConversationId
      );
      
      if (conversationToOpen) {
        setSelectedConversation(conversationToOpen);
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, conversations]);

  const fetchMyAssignments = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/v1/conversations/my-assignments', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setConversations(data.data || []);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching assignments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Check bot pause status
  const checkBotPauseStatus = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/conversations/${conversationId}/bot-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setBotPaused(data.data.bot_paused);
        setBotPauseRemaining(data.data.remaining_seconds);
      }
    } catch (error) {
      console.error('Error checking bot pause status:', error);
    }
  };

  // 🆕 Toggle bot pause
  const toggleBotPause = async () => {
    if (!selectedConversation) return;
    const token = localStorage.getItem('token');
    const convId = selectedConversation.conversation_id || selectedConversation.id;
    
    try {
      const response = await fetch(`/api/v1/conversations/${convId}/bot-pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ paused: !botPaused })
      });
      const data = await response.json();
      if (data.success) {
        setBotPaused(data.bot_paused);
        if (data.bot_paused) {
          setBotPauseRemaining(300);
        } else {
          setBotPauseRemaining(null);
        }
      }
    } catch (error) {
      console.error('Error toggling bot pause:', error);
    }
  };

  const fetchMessages = async (conversationId, silent = false) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`/api/v1/conversations/${conversationId}/messages?after_id=0`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setMessages(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      if (!silent) {
        alert('Failed to load messages');
      }
    }
  };

  const handleSelectConversation = (assignment) => {
    setSelectedConversation(assignment);
    setShowReplyBox(false);
    setReplyText('');
    // Reset bot pause state for new conversation
    setBotPaused(false);
    setBotPauseRemaining(null);
    setBotPauseWarning(null);
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) {
      alert('Please enter a message');
      return;
    }

    const token = localStorage.getItem('token');
    setSending(true);

    try {
      const response = await fetch(`/api/v1/conversations/${selectedConversation.conversation_id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: replyText,
          message_type: 'agent'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      
      if (data.success) {
        // Immediately refresh messages
        await fetchMessages(selectedConversation.conversation_id);
        
        // 🆕 Refresh bot pause timer (agent sent a message = reset 5 min countdown)
        if (botPaused) {
          setBotPauseRemaining(300); // Reset to 5 minutes
        }
        
        setReplyText('');
        setShowReplyBox(false);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!selectedConversation) return;

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`/api/v1/conversations/${selectedConversation.conversation_id}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to complete conversation');
      }

      const data = await response.json();
      
      if (data.success) {
        // 🆕 Reset bot pause state on complete
        setBotPaused(false);
        setBotPauseRemaining(null);
        setBotPauseWarning(null);
        
        alert('Conversation marked as complete!');
        await fetchMyAssignments();
        setSelectedConversation(null);
        setMessages([]);
      } else {
        throw new Error(data.message || 'Failed to complete conversation');
      }
    } catch (err) {
      console.error('Error completing conversation:', err);
      alert('Failed to complete conversation: ' + err.message);
    }
  };

  const getTimeSince = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const minutes = Math.floor((now - then) / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <div>
            <h1 style={{ margin: '0 0 10px 0' }}>My Conversations ({conversations.length})</h1>
            <p style={{ margin: 0, color: '#6c757d' }}>View your assigned customer conversations</p>
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '40px', 
            borderRadius: '10px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              margin: '0 auto 20px',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#6c757d' }}>Loading conversations...</p>
          </div>
        ) : error ? (
          <div style={{
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            color: '#721c24',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <strong>Error:</strong> {error}
          </div>
        ) : conversations.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            padding: '60px 40px',
            textAlign: 'center',
            borderRadius: '10px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>💬</div>
            <h2 style={{ margin: '0 0 10px 0', color: '#212529' }}>No Conversations</h2>
            <p style={{ color: '#6c757d', margin: 0 }}>You don't have any assigned conversations yet</p>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '20px' }}>
            {/* Conversations List */}
            <div style={{ 
              width: '350px', 
              backgroundColor: 'white', 
              borderRadius: '10px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto'
            }}>
              <div style={{ padding: '15px', borderBottom: '1px solid #e9ecef' }}>
                <h3 style={{ margin: 0, fontSize: '16px' }}>Active Conversations</h3>
              </div>
              {conversations.map((conv) => (
                <div
                  key={conv.conversation_id}
                  onClick={() => handleSelectConversation(conv)}
                  style={{
                    padding: '15px',
                    borderBottom: '1px solid #f1f3f5',
                    cursor: 'pointer',
                    backgroundColor: selectedConversation?.conversation_id === conv.conversation_id ? '#e7f3ff' : 'transparent',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedConversation?.conversation_id !== conv.conversation_id) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedConversation?.conversation_id !== conv.conversation_id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div>
                      <strong style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                        {conv.phone_number || conv.session_id}
                      </strong>
                      {conv.user_name && (
                        <span style={{ fontSize: '12px', color: '#6c757d' }}>
                          {conv.user_name}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '11px', color: '#adb5bd' }}>
                      {getTimeSince(conv.assigned_at)}
                    </span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      backgroundColor: conv.language === 'ar' ? '#e3f2fd' : '#f3e5f5',
                      color: conv.language === 'ar' ? '#1976d2' : '#7b1fa2'
                    }}>
                      {conv.language === 'ar' ? 'AR' : 'EN'}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      color: '#6c757d'
                    }}>
                      ID: {conv.conversation_id}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Messages Panel */}
            <div style={{ 
              flex: 1, 
              backgroundColor: 'white', 
              borderRadius: '10px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100vh - 200px)'
            }}>
              {!selectedConversation ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#adb5bd'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>💬</div>
                    <p>Select a conversation to view messages</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Conversation Header */}
                  <div style={{ 
                    padding: '20px', 
                    borderBottom: '1px solid #e9ecef',
                    flexShrink: 0
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h3 style={{ margin: '0 0 5px 0' }}>
                          {selectedConversation.phone_number || selectedConversation.session_id}
                        </h3>
                        {selectedConversation.user_name && (
                          <p style={{ margin: '0 0 10px 0', color: '#495057', fontSize: '15px' }}>
                            {selectedConversation.user_name}
                          </p>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#28a745',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#28a745',
                          display: 'inline-block',
                          animation: 'pulse 2s infinite'
                        }}></span>
                        Auto-refreshing
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '20px', fontSize: '14px', color: '#6c757d', marginTop: '10px' }}>
                      <span>
                        <strong>Language:</strong> {selectedConversation.language === 'ar' ? 'Arabic' : 'English'}
                      </span>
                      <span>
                        <strong>Messages:</strong> {messages.length}
                      </span>
                      <span>
                        <strong>Assigned:</strong> {getTimeSince(selectedConversation.assigned_at)}
                      </span>
                    </div>
                  </div>

                  {/* 🆕 Bot Pause Warning Banner - MOVED HERE (above messages) */}
                  {botPauseWarning && (
                    <div style={{
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      color: '#856404',
                      padding: '10px 15px',
                      borderRadius: '0',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      animation: 'pulse 1s infinite'
                    }}>
                      {botPauseWarning}
                    </div>
                  )}

                  {/* 🆕 Bot Pause Button + Status - MOVED HERE (above messages) */}
                  {selectedConversation && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 20px',
                      borderBottom: '1px solid #e9ecef',
                      backgroundColor: botPaused ? '#f8f9fa' : 'transparent',
                      flexShrink: 0
                    }}>
                      <button
                        onClick={toggleBotPause}
                        style={{
                          padding: '6px 14px',
                          backgroundColor: botPaused ? '#28a745' : '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {botPaused ? '▶️ تشغيل البوت' : '⏸️ إيقاف البوت'}
                      </button>
                      
                      {botPaused && botPauseRemaining && (
                        <span style={{
                          fontSize: '12px',
                          color: botPauseRemaining <= 60 ? '#dc3545' : '#6c757d',
                          fontWeight: botPauseRemaining <= 60 ? 'bold' : 'normal'
                        }}>
                          ⏱️ البوت يعود خلال: {Math.floor(botPauseRemaining / 60)}:{String(botPauseRemaining % 60).padStart(2, '0')}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Messages */}
                  <div style={{ 
                    flex: 1,
                    overflowY: 'auto', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px', 
                    padding: '15px',
                    marginBottom: '20px'
                  }}>
                    {messages.length === 0 ? (
                      <p style={{ textAlign: 'center', color: '#6c757d', padding: '20px' }}>
                        No messages yet
                      </p>
                    ) : (
                      <>
                        {messages.map((msg, idx) => (
                          <div key={idx} style={{ marginBottom: '15px' }}>
                            <div style={{
                              padding: '12px',
                              borderRadius: '8px',
                              backgroundColor: msg.message_type === 'customer' ? '#e3f2fd' : msg.message_type === 'ai' ? '#fff3cd' : '#d4edda',
                              borderLeft: `3px solid ${msg.message_type === 'customer' ? '#2196f3' : msg.message_type === 'ai' ? '#ffc107' : '#28a745'}`
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                marginBottom: '8px',
                                alignItems: 'center'
                              }}>
                                <strong style={{ fontSize: '14px' }}>
                                  {msg.message_type === 'customer' ? '👤 Customer' : msg.message_type === 'ai' ? '🤖 AI' : '💬 Agent'}
                                </strong>
                                <span style={{ fontSize: '11px', color: '#6c757d' }}>
                                  {new Date(msg.timestamp).toLocaleString()}
                                </span>
                              </div>
                              <div style={{ 
                                direction: selectedConversation.language === 'ar' ? 'rtl' : 'ltr',
                                textAlign: selectedConversation.language === 'ar' ? 'right' : 'left',
                                fontSize: '14px',
                                lineHeight: '1.5'
                              }}>
                                {msg.message_text}
                              </div>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Reply Box */}
                  {showReplyBox && (
                    <div style={{ marginBottom: '20px', flexShrink: 0, padding: '0 15px' }}>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={selectedConversation.language === 'ar' ? 'اكتب ردك هنا...' : 'Type your reply...'}
                        style={{
                          width: '100%',
                          minHeight: '100px',
                          padding: '12px',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          direction: selectedConversation.language === 'ar' ? 'rtl' : 'ltr',
                          textAlign: selectedConversation.language === 'ar' ? 'right' : 'left'
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            handleSendReply();
                          }
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                        Press Ctrl+Enter to send
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '10px', 
                    justifyContent: 'flex-end',
                    paddingTop: '15px',
                    borderTop: '1px solid #e9ecef',
                    flexShrink: 0,
                    padding: '15px'
                  }}>
                    {!showReplyBox ? (
                      <button
                        onClick={() => setShowReplyBox(true)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        💬 Reply
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setShowReplyBox(false);
                            setReplyText('');
                          }}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSendReply}
                          disabled={sending || !replyText.trim()}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: sending || !replyText.trim() ? '#6c757d' : '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: sending || !replyText.trim() ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          {sending ? '⏳ Sending...' : '📤 Send'}
                        </button>
                      </>
                    )}
                    <button
                      onClick={handleMarkComplete}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      ✅ Mark as Complete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Add pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Layout>
  );
};

export default MyConversations;